/**
 * ArtImage — the shared image load-in treatment ("the site doesn't feel
 * seamless" finding, 24 Jul: real art pops in the instant it decodes, no
 * screen anywhere fades it in). A plain `<img>` wrapped with an opacity
 * transition keyed off the native `load` event — no library, same grammar as
 * every other entrance in this codebase (--md-time-base + --md-ease-settle).
 *
 * Cached images fire `load` before React attaches the listener, so this reads
 * `img.complete` on mount first — otherwise an already-cached asset would
 * stay invisible forever waiting for an event that already fired.
 *
 * Scope note: this is the primitive, not a retrofit — existing screens keep
 * their current plain <img> tags until each is ported against its comp in
 * the journey-screens pass; wiring every existing image in one sweep here
 * would be a much larger, separately-risked change than "add the seamlessness
 * primitive." New art (AppChrome's logo lockup, Help's Kitman avatar) uses it
 * from today.
 */
import { useEffect, useRef, useState } from "react";
import type { ImgHTMLAttributes } from "react";

export interface ArtImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
}

export function ArtImage({ src, alt, style, className, ...rest }: ArtImageProps): JSX.Element {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(ref.current?.complete === true);
  }, [src]);

  return (
    <img
      ref={ref}
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      className={["mdui-art-image", loaded ? "mdui-art-image--loaded" : "", className]
        .filter(Boolean)
        .join(" ")}
      style={style}
      {...rest}
    />
  );
}
